import React, { useState, useCallback } from 'react';
import Code from '../../Code/Code';
import EmbedCodeCopyButton from '../../EmbedCodeCopyButton/EmbedCodeCopyButton';
import sdkUrls from '../../../../../const/sdkUrls';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import getHostUrlForIntegrations from '../../../../../services/mini/getHostUrlForIntegrations';

const alanSwiftPlayCode = 'self.button.playText("Hello")';

const alanSwiftSendCode =
    `let commandObject = ["command": "navigate", "screen": "settings"]
self.button.playCommand(commandObject)`;

const alanSwiftImportCode = 'import AlanSDK';

function getSwiftDefineCode() {
  return `fileprivate var button: AlanButton!

fileprivate var text: AlanText!  
`;
}

function getSwiftCode(alanKey, codeMode) {
  if (codeMode === 'simple') {
    return `/// Define the project key
let config = AlanConfig(key: "${alanKey}", host:"${getHostUrlForIntegrations()}")
        
///  Init the Alan button
self.button = AlanButton(config: config)
        
/// Init the Alan text panel
self.text = AlanText(frame: CGRect.zero)
        
/// Add the button and text panel to the view
self.view.addSubview(self.button)
self.button.translatesAutoresizingMaskIntoConstraints = false
self.view.addSubview(self.text)
self.text.translatesAutoresizingMaskIntoConstraints = false
        
/// Align the button and text panel on the view
let views = ["button" : self.button!, "text" : self.text!]
let verticalButton = NSLayoutConstraint.constraints(withVisualFormat: "V:|-(>=0@299)-[button(64)]-40-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
let verticalText = NSLayoutConstraint.constraints(withVisualFormat: "V:|-(>=0@299)-[text(64)]-40-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
let horizontalButton = NSLayoutConstraint.constraints(withVisualFormat: "H:|-(>=0@299)-[button(64)]-20-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
let horizontalText = NSLayoutConstraint.constraints(withVisualFormat: "H:|-20-[text]-20-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
self.view.addConstraints(verticalButton + verticalText + horizontalButton + horizontalText)
    
/// Handle commands from Alan AI Studio
self.button.onCommand = { command in
  guard let commandName = command?["command"] as? String else {
    return
  }
  print(commandName)
}
`;
  } else {
    return `/// Define the project key and additional params
let config = AlanConfig(key: "${alanKey}", host:"${getHostUrlForIntegrations()}", dataObject: ["data":"your auth data if needed"])
    
///  Init the Alan button
self.button = AlanButton(config: config)
    
/// Init the Alan text panel
self.text = AlanText(frame: CGRect.zero)
    
/// Add the button and text panel to the view
self.view.addSubview(self.button)
self.button.translatesAutoresizingMaskIntoConstraints = false
self.view.addSubview(self.text)
self.text.translatesAutoresizingMaskIntoConstraints = false
    
/// Align the button and text panel on the view
let views = ["button" : self.button!, "text" : self.text!]
let verticalButton = NSLayoutConstraint.constraints(withVisualFormat: "V:|-(>=0@299)-[button(64)]-40-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
let verticalText = NSLayoutConstraint.constraints(withVisualFormat: "V:|-(>=0@299)-[text(64)]-40-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
let horizontalButton = NSLayoutConstraint.constraints(withVisualFormat: "H:|-(>=0@299)-[button(64)]-20-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
let horizontalText = NSLayoutConstraint.constraints(withVisualFormat: "H:|-20-[text]-20-|", options: NSLayoutConstraint.FormatOptions(), metrics: nil, views: views)
self.view.addConstraints(verticalButton + verticalText + horizontalButton + horizontalText)

/// Handle commands from Alan AI Studio
self.button.onCommand = { command in
  guard let commandName = command?["command"] as? String else {
    return
  }
  print(commandName)
}

/// Handle events
self.button.onEvent = { event in
  guard let eventData = event?.data(using: .utf8, allowLossyConversion: false),
    let eventDict = try? JSONSerialization.jsonObject(with: eventData, options: .mutableContainers)
  else {
    return
  }
  print(eventDict)
}

/// Handle button state
self.button.onButtonState = { state in
  print(state.rawValue)
}
`;
  }
}

function getSwiftApiCode() {
  return `/// Activate Alan Button programmatically
func activate() {
  self.button.activate()
}
  
/// Deactivate Alan Button programmatically
func deactivate() {
  self.button.deactivate()
}
  
/// Play any text via Alan Button
func playText() {
  /// Provide text as string param
  self.button.playText("Hi")
}
  
/// Execute any command locally
func playCommand() {
  /// Provide any params
  self.button.playCommand(["action":"openSomePage"])
}
  
/// Call projectAPI.funcName from Alan AI Studio script
func callProjectApi() {
  /// Provide any params
  self.button.callProjectApi("script::funcName", withData: ["data":"your data"], callback: nil)
}
  
/// Set up a visual state
func setVisualState() {
  /// Provide any params
  self.button.setVisualState(["data":"your data"])
}
`;
}

const codeModes = [
  { name: 'Simple Example', key: 'simple' },
  { name: 'Advanced Example', key: 'advanced' }
];

export function IosTab({ alanKey }) {

    const [codeMode, setCodeMode] = useState('simple');

    const changeCodeModeCb = useCallback((codeModeObj) => {
      setCodeMode(codeModeObj.key);
    }, []);

    return <div className="embed-code-tab">
        <div className="embed-code-tab_line">
                <div className="embed-code-tab_description">
                </div>
                <div className="embed-code-tab_dash"></div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_not-highlighted-code">
                        <Code>
                            {
                                '\n'
                            }
                        </Code>
                    </div>
                </div>

            </div>
            <ComboBox values={codeModes}
                valueKey="key"
                valueName="name"
                classes="code-mode-combo-box light-blue-with-border"
                selectedValueId={codeMode}
                onValueSelect={changeCodeModeCb}
                renderItem={(codeModeItem) => {
                    return <div style={{ paddingLeft: '8px' }}
                        key={codeModeItem.key}>
                        {codeModeItem.name}
                    </div>;
                }} />
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                XCode project setup
            </div>
            <div className="embed-code-tab_dash embed-code-desc4" />
            <div className="embed-code-tab_code text-desc">
                <div className="embed-code-tab_desc">
                    Download iOS SDK framework <a href={sdkUrls.ios}>here</a>
                    <br></br>
                    Copy framework to your project
                    <br></br>
                    Set the “Embed” option for the framework to “Embed & Sign” (“General” tab for your target).
                    <br></br>
                    Add the “Privacy – Microphone Usage Description” key and provide the key description (“Info” tab for your target, must be added to access the microphone).
                    <br></br>
                    Enable the “Audio, AirPlay, and Picture in Picture” background mode (“Signing and Capabilities” tab for your target).
                    <br></br>
                    <br></br>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc5">
                Import AlanSDK framework
                </div>
            <div className="embed-code-tab_dash embed-code-desc5">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="ios" type="code">
                        {alanSwiftImportCode}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={alanSwiftImportCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc6">
                Define AlanButton variable
                </div>
            <div className="embed-code-tab_dash embed-code-desc6">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="ios" type="code">
                        {getSwiftDefineCode()}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={getSwiftDefineCode()} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc7">
                Setup AlanButton in viewDidLoad()
                </div>
            <div className="embed-code-tab_dash embed-code-desc7">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="ios" type="code">
                        {getSwiftCode(alanKey, codeMode)}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={getSwiftCode(alanKey, codeMode)} />
                    </div>
                </div>
            </div>
        </div>
        {(() => {
          if (codeMode === 'advanced') {
              return <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc7">
                    AlanButton common API
                    </div>
                <div className="embed-code-tab_dash embed-code-desc7">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <Code highlighted={true} category="ios" type="code">
                          {getSwiftApiCode()}
                        </Code>
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={getSwiftApiCode()} />
                        </div>
                    </div>
                </div>
            </div>;
          } else {
            return <></>;
          }
        })()}
    </div>;
}
